/**
 * 文件定位：
 * 1. 当前文件位于 Worker 倒角求解 class 层；
 * 2. 负责 SolidWorks 等距面倒角的 Puppy CAD 内核 构建；
 * 3. Partial Edge Parameters 的几何目标解析委托给已有 offset-face-partial 模块。
 *
 * 文件职责：
 * 1. 校验等距面倒角距离；
 * 2. 解析等距面/局部边目标 Shape；
 * 3. 按对称/非对称模式对解析出的目标边执行 Add_2/Add_3 倒角并释放中间资源。
 */

import { resolveOffsetFaceChamferTargets } from '../../offset-face-partial';
import { fusePartialBlendShapes } from '../../partial-edge-shape-fusion';
import {
  assertChamferBuilderResultSafe,
  buildProgressRange,
  safeDelete,
} from '../functions/chamferPuppyKernel';
import { resolveOffsetFaceSupportFace } from '../functions/offsetFaceSupportFace';
import { unifyPartialEdgeSameDomainShape } from '../../same-domain-unification';
import type { PartialEdgeBlendTarget } from '../../partial-edge-targets';
import type { ChamferKernelInstance, ChamferShapeBuildInput } from '../types/ChamferShapeBuildInput';
import { ChamferSolver } from './ChamferSolver';

/**
 * 等距面倒角求解器。
 */
export class OffsetFaceChamferSolver extends ChamferSolver {
  
  public readonly type = 'offset-face' as const;

  /**
   * 在单个 Solid 上执行等距面倒角。
   *
   * 设计说明：
   * 1. Partial Edge Parameters 会把源体切成多个 Solid；
   * 2. Puppy CAD 内核 Chamfer Builder 需要在边段所属的 Solid 上注册边，否则支撑面解析和 Add_3 都可能落到错误域；
   * 3. 因此这里不直接依赖整体 targetShape，而是由上层按 solidGroup 分发。
   *
   * @param input 单 Solid 求解输入。
   * @returns 求解后的 Shape。
   */
  private buildOnSingleShape(input: {
    puppy: ChamferKernelInstance;
    sourceShape: unknown;
    sourceMetadata: Record<string, unknown>;
    rootSourceShape: unknown;
    rootSourceMetadata: Record<string, unknown>;
    definition: ChamferFeatureDefinition;
    edgeTargets: PartialEdgeBlendTarget[];
    useSplitTopologySupport: boolean;
  }): unknown {
    if (input.edgeTargets.length === 0) {
      throw new Error('等距面倒角缺少可注册的目标边');
    }

    const builder = new input.puppy.BRepFilletAPI_MakeChamfer(input.sourceShape);
    const progress = buildProgressRange(input.puppy);
    const isAsymmetric = input.definition.symmetric === false;

    try {
      input.edgeTargets.forEach(({ edgeShape, selection }) => {
        if (!isAsymmetric) {
          builder.Add_2(input.definition.distance as number, edgeShape);
          return;
        }

        const supportFace = resolveOffsetFaceSupportFace({
          puppy: input.puppy,
          targetShape: input.sourceShape,
          targetMetadata: input.sourceMetadata,
          sourceShape: input.rootSourceShape,
          sourceMetadata: input.rootSourceMetadata,
          selection,
          edgeShape,
          useSplitTopologySupport: input.useSplitTopologySupport,
          /**
           * SolidWorks 的非对称等距面倒角允许沿另一侧支撑面翻转方向。
           * 方向必须在 worker 支撑面解析层处理，避免 UI 参与拓扑顺序判断。
           */
          preferredFaceOffset: input.definition.flipDirection === true ? 1 : 0,
        });
        builder.Add_3(
          input.definition.distance as number,
          input.definition.distance2 as number,
          edgeShape,
          supportFace,
        );
      });
      builder.Build(progress);
      return assertChamferBuilderResultSafe(input.puppy, builder);
    } finally {
      safeDelete(builder);
      safeDelete(progress);
    }
  }

  /**
   * 校验等距面倒角距离参数。
   *
   * @param definition 倒角定义。
   */
  private assertDistanceParameters(definition: ChamferFeatureDefinition): void {
    if (typeof definition.distance !== 'number' || !Number.isFinite(definition.distance) || definition.distance <= 0) {
      throw new Error('等距面倒角缺少有效距离');
    }
    if (
      definition.symmetric === false
      && (
        typeof definition.distance2 !== 'number'
        || !Number.isFinite(definition.distance2)
        || definition.distance2 <= 0
      )
    ) {
      throw new Error('等距面非对称倒角缺少有效第二距离');
    }
  }

  /**
   * 对局部边线分割后的结果执行同域统一。
   *
   * @param puppy Puppy CAD 内核 实例。
   * @param shape 待统一 Shape。
   * @param enabled 是否需要同域统一。
   * @returns 统一后的 Shape。
   */
  private finalizePartialShape(puppy: ChamferKernelInstance, shape: unknown, enabled: boolean): unknown {
    return enabled
      ? unifyPartialEdgeSameDomainShape({
        puppy,
        shape,
        operationLabel: '等距面部分倒角',
      })
      : shape;
  }

  /**
   * @inheritdoc
   */
  public build(input: ChamferShapeBuildInput): unknown {
    const { puppy, sourceShape, sourceMetadata, sourceObjectId, definition } = input;
    this.assertDistanceParameters(definition);

    const targets = resolveOffsetFaceChamferTargets({
      puppy,
      sourceShape,
      sourceMetadata,
      definition,
      sourceObjectId,
    });

    const buildGroup = (group: { solidShape: unknown; edgeTargets: PartialEdgeBlendTarget[] }) => (
      this.buildOnSingleShape({
        puppy,
        sourceShape: group.solidShape,
        sourceMetadata,
        rootSourceShape: sourceShape,
        rootSourceMetadata: sourceMetadata,
        definition,
        edgeTargets: group.edgeTargets,
        useSplitTopologySupport: targets.requiresSameDomainUnification,
      })
    );

    try {
      if (targets.solidGroups.length === 1 && targets.remainderSolidShapes.length === 0) {
        return this.finalizePartialShape(
          puppy,
          buildGroup(targets.solidGroups[0]!),
          targets.requiresSameDomainUnification,
        );
      }

      const partialResultShapes: unknown[] = [];
      try {
        targets.solidGroups.forEach((group) => {
          partialResultShapes.push(buildGroup(group));
        });
        const fusedShape = fusePartialBlendShapes({
          puppy,
          shapes: [...partialResultShapes, ...targets.remainderSolidShapes],
          temporaryInputShapes: partialResultShapes,
          operationLabel: '等距面部分倒角',
        });
        return this.finalizePartialShape(puppy, fusedShape, targets.requiresSameDomainUnification);
      } catch (error) {
        /**
         * 若某个分组求解中途失败，还没有进入通用 Fuse 清理链路。
         * 这里释放已完成的局部结果，保证失败路径也不泄漏 Puppy CAD 内核 Shape。
         */
        partialResultShapes.forEach((shape) => safeDelete(shape));
        throw error;
      }
    } finally {
      targets.cleanup();
    }
  }
}
